import { Icon } from "@iconify/react";
import { useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/locales/i18n-provider";
import { cn } from "@/utils";
import type { TicketActivityItem } from "../utils/ticket-presentation";
import { TicketActivityEntry } from "./ticket-activity-entry";

type ResolutionEvent = Extract<TicketActivityItem, { kind: "event" }>;

export function TicketResolutionActivity({ activity }: { activity: ResolutionEvent & { resolution: NonNullable<ResolutionEvent["resolution"]> } }) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const resultId = useId();
  const { result, feedbackMethodLabel, feedbackMethodIcon } = activity.resolution;
  const author = activity.author || t("tickets.activity.systemAuthor");
  const collapsible = result.length > 400 || result.split("\n").length > 6;

  return (
    <TicketActivityEntry
      author={author}
      avatarUrl={activity.avatarUrl}
      initials={activity.initials || author.slice(0, 1)}
      meta={
        <span className="inline-flex items-center gap-1 text-xs font-medium text-success">
          <Icon className="size-3.5" icon="tabler:circle-check" />
          {t("tickets.status.resolved")}
        </span>
      }
      time={activity.time}
    >
      <dl className="mt-2 max-w-2xl rounded-lg border border-success/20 bg-success/10 px-3 py-2 text-sm">
        <div>
          {/* <dt className="text-xs font-medium text-success">{t("tickets.resolution.resultLabel")}</dt> */}
          <div className={cn("mt-1 whitespace-pre-wrap wrap-break-word leading-6 text-success", collapsible && !expanded && "line-clamp-6")} id={resultId}>
            {result}
          </div>
          {collapsible ? (
            <Button aria-controls={resultId} aria-expanded={expanded} className="mt-1 px-0" size="sm" variant="link" onClick={() => setExpanded(!expanded)}>
              {t(expanded ? "tickets.resolution.collapseResult" : "tickets.resolution.expandResult")}
            </Button>
          ) : null}
        </div>
        <div className="mt-2 text-xs leading-5 text-success/80">
          <dt className="sr-only">{t("tickets.resolution.feedbackMethodLabel")}</dt>
          <dd className="flex min-w-0 items-start gap-1.5">
            <Icon className="mt-0.5 size-3.5 shrink-0" icon={feedbackMethodIcon ?? "tabler:messages"} />
            <span className="min-w-0 whitespace-pre-wrap wrap-break-word">{t("tickets.resolution.feedbackSummary", { method: feedbackMethodLabel })}</span>
          </dd>
        </div>
      </dl>
    </TicketActivityEntry>
  );
}
